import { useMemo } from 'react';
import { CircleCheck } from 'lucide-react';

import type {
  VideoErrorPie,
  VideoHistoricalSeries,
  VideoJob,
  VideoStats,
} from '~/modules/query/listingVideos';
import {
  useGetDimensionSeriesQuery,
  useGetListingTypeSeriesQuery,
  useGetVideoErrorPieQuery,
  useGetVideoFailedJobsQuery,
  useGetVideoQueuedJobsQuery,
  useGetVideoStatsQuery,
} from '~/modules/query/listingVideos';
import { PageHeader, PageShell } from '~/components/layout';
import {
  DashboardCard,
  DonutCard,
  FailedSections,
  LineChartCard,
  SECTION_FAILED,
  StatTile,
  StatTileSkeleton,
  StatsRow,
  type DonutSlice,
  type LineChartCardProps,
} from '~/components/dashboard';
import { EmptyState, PanelTable, RefreshControls, type PanelTableColumn } from '~/components/ui';
import {
  DURATION_KEYS,
  LISTING_VIDEO_HEADER,
  LIST_OF_CARDS,
  PIE_TOKENS,
} from '~/components/Constants/videodashboardConstants';
import { useAutoRefresh } from '~/hooks/useAutoRefresh';
import { formatLocalTime } from '~/lib/datetime';
import { formatDuration, plural } from '~/lib/format';
import { toStatValue } from '~/components/Constants/homeConstants';

type Widget = 'stats' | 'quality' | 'type' | 'errors' | 'failed' | 'queued';

const WIDGET_NAMES: Record<Widget, string> = {
  stats: 'video counters',
  quality: 'videos by quality',
  type: 'videos by listing type',
  errors: 'errors by error code',
  failed: 'failed jobs',
  queued: 'queued jobs',
};

const mono = (value: unknown) => <span className="font-mono text-xs">{String(value ?? '')}</span>;

const FAILED_COLUMNS: PanelTableColumn<VideoJob>[] = [
  { header: 'Job', render: (job) => mono(job._id) },
  { header: 'MLS #', render: (job) => mono(job.data?.mls_number) },
  { header: 'Listing', render: (job) => mono(job.data?.listing_id) },
  { header: 'Received', render: (job) => job.receivedTime ?? '' },
  { header: 'Account', render: (job) => mono(job.data?.account_id) },
  { header: 'Created by', render: (job) => job.data?.created_by ?? '' },
  { header: 'Template', render: (job) => job.template ?? '' },
  { header: 'Error', render: (job) => `${job.errorCode ?? ''} ${job.error?.message ?? ''}` },
];

const QUEUED_COLUMNS: PanelTableColumn<VideoJob>[] = [
  { header: 'Job', render: (job) => mono(job._id) },
  { header: 'Status', render: (job) => job.status ?? '' },
  { header: 'Received', render: (job) => job.receivedTime ?? '' },
  { header: 'MLS #', render: (job) => mono(job.data?.mls_number) },
  { header: 'Listing', render: (job) => mono(job.data?.listing_id) },
  { header: 'Address', render: (job) => job.data?.listing_address ?? '' },
  { header: 'Account', render: (job) => mono(job.data?.account_id) },
  { header: 'Created by', render: (job) => job.data?.created_by ?? '' },
  { header: 'Template', render: (job) => job.template ?? '' },
];

// 'MM-DD-YYYY' -> the viewer's short date ("Oct 8").
const dayLabel = (id: string) => {
  const [month, day, year] = id.split('-').map(Number);
  return formatLocalTime(new Date(year, month - 1, day), { month: 'short', day: 'numeric' });
};

// {seriesKey: [{_id: 'MM-DD-YYYY', count}]} -> nivo line series
const modifiedChartData = (chartRes: VideoHistoricalSeries): LineChartCardProps['series'] => {
  const dateFilter = Object.values(chartRes)
    .flat()
    .map((item) => item._id);
  const uniqueDates = [...new Set(dateFilter)];
  return Object.keys(chartRes).map((resolution) => ({
    id: resolution,
    data: uniqueDates.map((date) => {
      const item = chartRes[resolution].find((entry) => entry._id === date);
      return { x: dayLabel(date), y: item?.count ?? 0 };
    }),
  }));
};

// {totalCount, errorCode: {count, percent}} -> nivo pie slices
const toPieSlices = (pie: VideoErrorPie): DonutSlice[] => {
  const { totalCount, ...errorCodes } = pie;
  return Object.entries(errorCodes).map(([errorCode, info], index) => {
    const entry = typeof info === 'object' ? info : undefined;
    return {
      id: errorCode,
      label: errorCode,
      value: entry?.count ?? entry?.percent ?? 0,
      token: PIE_TOKENS[index % PIE_TOKENS.length],
    };
  });
};

const statValue = (key: string, data: VideoStats | undefined) => {
  if (key === 'lastProcessedTimestamp') return formatLocalTime(data?.[key] as string | undefined);
  return DURATION_KEYS.has(key) ? formatDuration(data?.[key]) : toStatValue(data?.[key]);
};

const videosTotal = (series: LineChartCardProps['series'] | undefined) =>
  plural(
    (series ?? []).reduce((sum, line) => sum + line.data.reduce((n, p) => n + Number(p.y), 0), 0),
    'video',
  );

export const VideoDashboard = () => {
  const stats = useGetVideoStatsQuery();
  const qualitySeries = useGetDimensionSeriesQuery({});
  const listingTypeSeries = useGetListingTypeSeriesQuery({});
  const errorPie = useGetVideoErrorPieQuery({});
  const failedJobsQuery = useGetVideoFailedJobsQuery();
  const queuedJobsQuery = useGetVideoQueuedJobsQuery();

  const videosStats = stats.data;
  const videoQualityData = useMemo(
    () => (qualitySeries.data ? modifiedChartData(qualitySeries.data) : undefined),
    [qualitySeries.data],
  );
  const listingType = useMemo(
    () => (listingTypeSeries.data ? modifiedChartData(listingTypeSeries.data) : undefined),
    [listingTypeSeries.data],
  );
  const errorPieData = useMemo(
    () => (errorPie.data ? toPieSlices(errorPie.data) : undefined),
    [errorPie.data],
  );
  const failedJobs = failedJobsQuery.data;
  const queuedJobs = queuedJobsQuery.data;

  // Each widget loads independently (DESIGN.md 7.5); `failed` names the ones whose query rejected.
  const widgetQueries = [
    { widget: 'stats', query: stats },
    { widget: 'quality', query: qualitySeries },
    { widget: 'type', query: listingTypeSeries },
    { widget: 'errors', query: errorPie },
    { widget: 'failed', query: failedJobsQuery },
    { widget: 'queued', query: queuedJobsQuery },
  ] as const;
  const failed = widgetQueries.filter((entry) => entry.query.isError).map((entry) => entry.widget);
  const refetchAll = () => widgetQueries.forEach((entry) => entry.query.refetch());
  const { auto, setAuto } = useAutoRefresh('listing-videos', refetchAll);
  const updatedAt = Math.max(
    0,
    ...widgetQueries.map((entry) => entry.query.fulfilledTimeStamp ?? 0),
  );

  return (
    <PageShell>
      <PageHeader
        title="Listing videos"
        description={LISTING_VIDEO_HEADER}
        breadcrumbs={[{ label: 'Listing videos' }]}
        extra={
          <RefreshControls
            onRefresh={refetchAll}
            fetching={widgetQueries.some((entry) => entry.query.isFetching)}
            updatedAt={updatedAt}
            auto={auto}
            onAutoChange={setAuto}
            subject="listing-videos"
          />
        }
      />

      <FailedSections
        failed={failed.map((widget) => WIDGET_NAMES[widget])}
        onRetry={() =>
          widgetQueries.forEach((entry) => entry.query.isError && entry.query.refetch())
        }
        testId="listing-videos"
      />

      <section aria-label="Listing videos dashboard" className="grid grid-cols-12 gap-4 md:gap-6">
        <div className="col-span-12">
          {stats.isError ? (
            <DashboardCard title="Video counters" error={SECTION_FAILED} />
          ) : (
            <StatsRow ariaLabel="Video counters">
              {Object.entries(LIST_OF_CARDS).map(([key, label]) =>
                stats.isLoading ? (
                  <StatTileSkeleton key={key} />
                ) : (
                  <StatTile key={key} label={label} value={statValue(key, videosStats)} />
                ),
              )}
            </StatsRow>
          )}
        </div>

        <LineChartCard
          className="col-span-12 lg:col-span-6"
          title="Videos rendered by video quality"
          ariaLabel="Videos rendered per day by video quality"
          loading={qualitySeries.isLoading}
          error={qualitySeries.isError ? SECTION_FAILED : undefined}
          series={videoQualityData ?? []}
          integers
          summary={`${videosTotal(videoQualityData)} rendered in this period.`}
          emptyTitle="No videos in the last 14 days"
          emptyDescription="Videos rendered in the last 14 days appear here."
        />
        <LineChartCard
          className="col-span-12 lg:col-span-6"
          title="Videos rendered by listing type"
          ariaLabel="Videos rendered per day by listing type"
          loading={listingTypeSeries.isLoading}
          error={listingTypeSeries.isError ? SECTION_FAILED : undefined}
          series={listingType ?? []}
          integers
          summary={`${videosTotal(listingType)} rendered in this period.`}
          emptyTitle="No videos in the last 14 days"
          emptyDescription="Videos rendered in the last 14 days appear here."
        />

        <DonutCard
          className="col-span-12"
          title="Errors by error code"
          ariaLabel="Errors by error code"
          loading={errorPie.isLoading}
          error={errorPie.isError ? SECTION_FAILED : undefined}
          slices={errorPieData ?? []}
          unit={{ one: 'error', other: 'errors' }}
          emptyTitle="No errors"
          emptyDescription="Failed renders are grouped here by error code."
        />

        <DashboardCard
          className="col-span-12"
          loading={failedJobsQuery.isLoading}
          error={failedJobsQuery.isError ? SECTION_FAILED : undefined}
          title="Failed jobs"
        >
          {failedJobs?.length ? (
            <PanelTable
              columns={FAILED_COLUMNS}
              data={failedJobs}
              maxHeight="24rem"
              ariaLabel="Failed jobs"
              emptyMessage="No failed jobs"
              emptyDescription="Jobs that fail to render are listed here."
            />
          ) : (
            <EmptyState
              icon={
                <CircleCheck
                  className="size-8 text-success-600 dark:text-success-500"
                  aria-hidden="true"
                />
              }
              title="No failed jobs"
              description="Jobs that fail to render are listed here."
            />
          )}
        </DashboardCard>

        <DashboardCard
          className="col-span-12"
          loading={queuedJobsQuery.isLoading}
          error={queuedJobsQuery.isError ? SECTION_FAILED : undefined}
          title={`Queued / processing jobs (${queuedJobs?.length ?? 0}${(queuedJobs?.length ?? 0) >= 500 ? '+' : ''})`}
        >
          {queuedJobs?.length ? (
            <PanelTable
              columns={QUEUED_COLUMNS}
              data={queuedJobs}
              maxHeight="24rem"
              ariaLabel="Queued and processing jobs"
              emptyMessage="No queued jobs"
              emptyDescription="Jobs waiting to render or rendering appear here."
            />
          ) : (
            <EmptyState
              icon={
                <CircleCheck
                  className="size-8 text-success-600 dark:text-success-500"
                  aria-hidden="true"
                />
              }
              title="No queued jobs"
              description="Jobs waiting to render or rendering appear here."
            />
          )}
        </DashboardCard>
      </section>
    </PageShell>
  );
};
